import { Component, ViewChild } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { EPersona } from '@norfolk-southern/accessns-components';
import { Observable } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import SweetAlert2 from 'sweetalert2';

import { NSAccessorialInventoryConstants } from '@ruby/configs/accessorial-inventory.constants';
import { NSCommons } from '@ruby/configs/common.constants';
import { NSMessages } from '@ruby/configs/messages.constants';
import {
  IAccessorialInventory,
  IAccessorialRequest,
  ILeaseTrackOverage
} from '@ruby/modules/accessorial-inventory/models/accessorial-inventory.interface';
import * as AccessorialInventoryActions from '@ruby/modules/accessorial-inventory/store/actions/accessorial-inventory.actions';
import * as fromAccessorialInventory from '@ruby/modules/accessorial-inventory/store/reducers/accessorial-inventory.reducer';
import * as AccessorialInventorySelectors from '@ruby/modules/accessorial-inventory/store/selectors/accessorial-inventory.selectors';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import { IIPAuthorizations } from '@ruby/modules/customer-select/models/ip-authorization.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { EGridTitle, EGridType } from '@ruby/shared/enums/grid.enums';
import { EInventoryRoute, EInventoryTab } from '@ruby/shared/enums/inventory-management.enums';
import { IGridPrintTitle } from '@ruby/shared/models/commons/grid.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-accessorial-inventory-container',
  templateUrl: './accessorial-inventory.container.html',
  styleUrls: ['./accessorial-inventory.container.scss']
})
export class AccessorialInventoryContainer {
  @ViewChild('matTabGroup') matTabGroup!: MatTabGroup;
  inventoryConfig = NSAccessorialInventoryConstants.inventoryConfig;
  customerSelected!: ICustomer;
  gridType = EGridType;
  name: EGridType = EGridType.INTRA_PLANT_SWITCH;
  activeTab: string = EInventoryTab.INTRA_PLANT_SWITCH;
  type?: EInventoryRoute;
  state!: fromAccessorialInventory.State;
  printTitle!: IGridPrintTitle;
  store$: Observable<{ state: fromAccessorialInventory.State; customerSelected?: ICustomer; authorizations?: IIPAuthorizations; type?: EInventoryRoute }>;

  constructor(private store: Store<fromAccessorialInventory.State>, private utilSvc: UtilsService, private router: Router) {
    this.store$ = this.store.select(AccessorialInventorySelectors.selectAccessorialWithAuth).pipe(
      debounceTime(1),
      tap((st: {
        state: fromAccessorialInventory.State; customerSelected?: ICustomer; authorizations?: IIPAuthorizations; type?: EInventoryRoute;
      }) => {
        this.setType(st.type);
        this.state = st.state;
        this.setSummary();
        this.setSelectedCustomer(st.customerSelected);
      })
    );
  }

  get monitorDate(): number {
    return this.type === EInventoryRoute.HISTORY ?
      this.utilSvc.getPreviousMonthStartDate().getTime() :
      this.utilSvc.getCurrentMonthStartDate().getTime();
  }

  /**
   * Update Summary
   *
   * @summary Updated summary totals
   * @params $tabEvent: MatTabChangeEvent
   * @returns void
   */
  updateSummary($tabEvent: MatTabChangeEvent): void {
    this.activeTab = $tabEvent.tab.textLabel;
    this.setName($tabEvent.tab.textLabel);
  }

  /**
   * Dispatch Load Accessorial
   *
   * @summary Dispatch an Load Accessorial action
   * @params monitoredDate: number
   * @returns void
   */
  dispatchLoadAccessorial(monitoredDate: number): void {
    this.store.dispatch(AccessorialInventoryActions.loadAccessorial({
      data: {
        classCode: this.customerSelected.locations[0].classCode,
        stationCode: this.customerSelected.locations[0].stationCode,
        monitoredDate
      } as IAccessorialRequest
    }));
  }

  private setSelectedCustomer(customerSelected?: ICustomer): void {
    if (customerSelected && customerSelected.type.name !== EPersona.IP) {
      SweetAlert2.fire({
        title: NSMessages.alert,
        text: NSMessages.notAuthorized,
        icon: 'error',
        heightAuto: false,
        confirmButtonColor: EColors.GOLD,
        willClose: () => { this.router.navigate(['./home/dashboard']); }
      });
    } else if (customerSelected && (this.customerSelected !== customerSelected)) {
      this.customerSelected = customerSelected;
      this.printTitle = NSCommons.gridHeader(customerSelected);
      this.dispatchLoadAccessorial(this.monitorDate);
      this.resetTab();
    }
  }

  private setType(type?: EInventoryRoute): void {
    if (type && this.type !== type) {
      this.type = type;
      if (this.customerSelected) {
        this.resetTab();
        this.dispatchLoadAccessorial(this.monitorDate);
      }
    }
  }

  private setName(name: string): void {
    switch (name) {
      case EInventoryTab.INTRA_PLANT_SWITCH:
        this.name = EGridType.INTRA_PLANT_SWITCH;
        this.setCount(this.state.intraPlants);
        break;
      case EInventoryTab.INTRA_TERMINAL_SWITCH:
        this.name = EGridType.INTRA_TERMINAL_SWITCH;
        this.setCount(this.state.intraTerminals);
        break;
      case EInventoryTab.PULL_TURN:
        this.name = EGridType.PULL_TURN;
        this.setCount(this.state.pullTurns);
        break;
      case EInventoryTab.PULL_WEIGHT:
        this.name = EGridType.PULL_WEIGHT;
        this.setCount(this.state.pullWeights);
        break;
      case EInventoryTab.ORDER_NOT_USED:
        this.name = EGridType.ORDER_NOT_USED;
        this.setCount(this.state.unusedOrders);
        break;
      case EInventoryTab.CHARGEABLE_REASON_CODE:
        this.name = EGridType.CHARGEABLE_REASON_CODE;
        this.setCount(this.state.chargeableCodes);
        break;
      case EInventoryTab.LATE_SURRENDER:
        this.name = EGridType.LATE_SURRENDER;
        this.setCount(this.state.lateSurrenders);
        break;
      case EInventoryTab.LEASE_TRACK_OVERAGES:
        this.name = EGridType.LEASE_TRACK_OVERAGES;
        this.setCount(this.state.leaseTrackOverages);
        break;
    }
  }

  private resetTab(): void {
    this.name = EGridType.INTRA_PLANT_SWITCH;
    this.activeTab = EInventoryTab.INTRA_PLANT_SWITCH;
    if (this.matTabGroup) {
      this.matTabGroup.selectedIndex = 0;
    }
  }

  private setSummary(): void {
    if (this.matTabGroup) {
      this.setName(this.activeTab);
    }
  }

  private setCount(data: Array<IAccessorialInventory | ILeaseTrackOverage>): void {
    const history: boolean = this.type === EInventoryRoute.HISTORY;
    let car = 0;
    let charges = 0;
    data.forEach(value => {
      car += 1;
      charges += value.charge;
    });
    this.inventoryConfig = {
      title: history ? EGridTitle.ACCESSORIAL_CHARGE_HISTORY : EGridTitle.ACCESSORIAL_CHARGES,
      car,
      charges,
      accessorial: { history }
    };
  }
}
